import React from 'react'
import {
  List,
  useTable,
  EditButton,
  ShowButton,
  DeleteButton,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Select } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, TableSurface, Pill, type Stat } from '../../theme/listkit'
import { CalendarDays, Sunrise, CalendarCheck } from '../../components/icons'

type EventSession = Tables<'event_sessions'>
type Event = Tables<'events'>

// Local shape for the one-row admin summary view. We don't use Tables<'…'>
// here because the generated view Row types it as `id: number | null`, which
// fails Refine's BaseRecord constraint (id must be a non-null BaseKey).
interface EventSessionsSummary {
  id: number
  total_sessions: number | null
  distinct_days: number | null
  distinct_events: number | null
}

export function EventSessionList() {
  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx): the sessions
  // table subscribes with liveMode:'auto' so a sub-event created or edited
  // elsewhere (another tab, the event Show page) shows up here without a
  // manual refresh. One bounded page of rows per refetch. The picker/summary
  // hooks below deliberately stay at the default (off).
  const { tableProps, filters, setFilters } = useTable<EventSession>({
    syncWithLocation: true,
    liveMode: 'auto',
  })
  const onRow = useRowShow('event_sessions')

  // Parent-event filter — options from a bounded events fetch (the calendar is
  // dozens of events, not thousands; 200 is generous headroom) cached for 5min.
  // Different resource from this page's useTable, and the global liveMode is
  // 'off', so this opens no realtime channel.
  const { result: filterEventsRes } = useList<Event>({
    resource: 'events',
    sorters: [{ field: 'start_date', order: 'desc' }],
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 300_000 },
  })
  const filterOptions = (filterEventsRes?.data ?? []).map((e) => ({
    label: e.title,
    value: e.id,
  }))
  // Reflect the active event_id filter (set here, or arriving via the
  // syncWithLocation querystring from the events-list count links).
  const activeEventFilter = filters.find(
    (f) => 'field' in f && f.field === 'event_id' && f.operator === 'eq',
  )
  const activeEventId = (activeEventFilter && 'value' in activeEventFilter
    ? (activeEventFilter.value as string)
    : undefined)

  // Resolve event_id -> title for ONLY the events referenced on the current
  // page. Derive the ids from the page's rows and fetch just those events (IN
  // filter) instead of pulling every event to build the label map.
  const eventIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          ((tableProps.dataSource ?? []) as EventSession[])
            .map((s) => s.event_id)
            .filter((v): v is string => Boolean(v)),
        ),
      ),
    [tableProps.dataSource],
  )
  const { result: eventResult } = useList<Event>({
    resource: 'events',
    filters: eventIds.length ? [{ field: 'id', operator: 'in', value: eventIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: eventIds.length > 0 },
  })

  const eventMap = React.useMemo<Record<string, string>>(() => {
    const items = (eventResult?.data ?? []) as Event[]
    return Object.fromEntries(items.map((e) => [e.id, e.title]))
  }, [eventResult])

  // Summary deck — read the precomputed totals + distinct counts straight off
  // the one-row admin summary view instead of fetching every session and
  // building Sets on the main thread.
  const { result: summaryRes } = useList<EventSessionsSummary>({
    resource: 'admin_event_sessions_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]

  const stats: Stat[] = [
    { label: 'Total Sessions', value: (summary?.total_sessions ?? 0).toLocaleString(), icon: CalendarDays, tone: 'gold' },
    { label: 'Days', value: (summary?.distinct_days ?? 0).toLocaleString(), icon: Sunrise, tone: 'jaguar' },
    { label: 'Events Covered', value: (summary?.distinct_events ?? 0).toLocaleString(), icon: CalendarCheck, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Events · Sub-events',
        description:
          'The agenda within each event — mixers, brunches, banquets and worship blocks. Each session has its own day, time and venue so attendees always know where to be.',
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>
            Parent event
          </span>
          <Select
            aria-label="Filter by parent event"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All events"
            style={{ minWidth: 240 }}
            options={filterOptions}
            value={activeEventId}
            onChange={(value?: string) =>
              setFilters(
                value ? [{ field: 'event_id', operator: 'eq', value }] : [],
                'replace',
              )
            }
          />
        </div>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<EventSession>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<EventSession>
              dataIndex="day"
              title="Day"
              render={(day: string | null) =>
                day ? <Pill tone="gold">{day}</Pill> : <span style={{ color: brand.sub }}>—</span>
              }
            />
            <Table.Column<EventSession>
              dataIndex="start_time"
              title="Start Time"
              render={(t: string | null) =>
                t ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{t}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<EventSession>
              dataIndex="location"
              title="Location"
              render={(loc: string | null) =>
                loc ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.sub }}>{loc}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<EventSession>
              dataIndex="event_id"
              title="Event"
              render={(eventId: string | null) =>
                eventId ? (eventMap[eventId] ?? eventId) : '—'
              }
            />
            <Table.Column<EventSession>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: EventSession) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
